import { FunctionComponent, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import capitalize from 'lodash/capitalize';
import { ReferrerQueryString } from '@fff-web/fff-analytics';
import { usePriceLockEligibility, useUserContext } from '@fff-web/fff-utilities';
import { type Subscription } from '@fff-web/growth-sdk';
import { LOCK_IN_RATE_PATH } from '../../../../utils/routePaths';
import {
  CANCEL_BOX_SUBSCRIPTION,
  CANCEL_CLUB_MEMBERSHIP,
  CANCEL_SUBSCRIPTION_LABEL,
  getCancelBoxSubscriptionLabel,
  REACTIVATE_MEMBERSHIP_LABEL,
  SUBSCRIPTION_STATUS,
} from '../../../constants';
import { ClubMembershipModal } from '../../ClubMembershipModal';
import { SubscriptionStatus } from '../../MyAccountPage';
import { CANCEL_FLOW_EVENT_NAME } from '../../utils/segment';
import ManageMembershipLink from './ManageMembershipLink';
import { trackClickCancelSubscription, trackClickReactivateMembership } from './trackEvents';
import * as manageMembershipStyles from './ManageMembership.module.css';

const REFERRER_QUERY_STRING = `?referrer=${ReferrerQueryString.myAccount}`;
const CANCEL_SUBSCRIPTION_AND_BOX_COPY =
  'Cancellation takes effect at the next billing cycle and does not impact pre-paid boxes.';
const CANCEL_SUBSCRIPTION_COPY =
  'By ending your account you will lose access to your FabFitFun benefits.';

type CancellableBoxSubscription = Subscription & { id: string };

type ManageMembershipProps = {
  isClubMember: boolean;
  clubMemberSubscriptionId?: string | null;
  subscriptionState: SubscriptionStatus;
  refetchClubMemberSubscription: () => void;
  boxSubscriptions?: Subscription[] | null;
};

const ManageMembership: FunctionComponent<ManageMembershipProps> = ({
  isClubMember,
  clubMemberSubscriptionId,
  subscriptionState,
  refetchClubMemberSubscription,
  boxSubscriptions,
}) => {
  const [isCancelMembershipModalOpen, setIsCancelMembershipModalOpen] = useState(false);
  const [cancelBoxModalStates, setCancelBoxModalStates] = useState<Record<string, boolean>>({});
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [canceledBoxIds, setCanceledBoxIds] = useState<string[]>([]);

  const { priceLock: priceLockFF } = useFlags();
  const { userInfo } = useUserContext();
  const { isEligible: isPriceLockEligible } = usePriceLockEligibility({
    priceLockFF,
    enabled: !isClubMember,
  });
  const { user } = userInfo;

  const isCanceled = subscriptionState === SUBSCRIPTION_STATUS.CANCELED;
  const isExpired = subscriptionState === SUBSCRIPTION_STATUS.EXPIRED;
  // Optimistically drop boxes whose cancel was just confirmed so their link disappears immediately,
  // without waiting for the subscriptions refetch (which can read stale data right after the mutation).
  // `id` is optional on the derived wire type (growth omits unset fields) but is what keys the
  // modal state and carries the cancellation itself, so a box without one cannot be managed. Drop
  // only those, once, so the rest of the component stays narrowing-free. `name` is deliberately
  // NOT required here — it only drives copy, and the label/modal both fall back.
  const visibleBoxSubscriptions = boxSubscriptions
    ?.filter((boxSub): boxSub is CancellableBoxSubscription => Boolean(boxSub.id))
    .filter((boxSub) => !canceledBoxIds.includes(boxSub.id));
  const hasVisibleBoxSubscriptions = Boolean(visibleBoxSubscriptions?.length);
  const cancelSubscriptionCopy = hasVisibleBoxSubscriptions
    ? `Cancel ${capitalize(user.subscriptionType)} Box Subscription`
    : CANCEL_SUBSCRIPTION_LABEL;
  const cancelSubscriptionSubcopy = hasVisibleBoxSubscriptions
    ? CANCEL_SUBSCRIPTION_AND_BOX_COPY
    : CANCEL_SUBSCRIPTION_COPY;

  const handleClickCancelClubMembershipCTA = () => {
    trackClickCancelSubscription(
      CANCEL_FLOW_EVENT_NAME.started,
      CANCEL_CLUB_MEMBERSHIP.label,
      CANCEL_CLUB_MEMBERSHIP.type,
    );
    setIsCancelMembershipModalOpen(true);
  };

  // Takes the subscription so the tracked label reuses `getCancelBoxSubscriptionLabel` and cannot
  // drift from the label the member actually clicked.
  const handleClickCancelBoxSubscriptionCTA = (boxSub: CancellableBoxSubscription) => {
    trackClickCancelSubscription(
      CANCEL_FLOW_EVENT_NAME.started,
      getCancelBoxSubscriptionLabel(boxSub),
      CANCEL_BOX_SUBSCRIPTION.type,
    );
    setCancelBoxModalStates((prev) => ({ ...prev, [boxSub.id]: true }));
  };

  // An add-on box (Beauty Edit, PupBox — anything but the seasonal core box, which MyAccountPage
  // already filters out) is billed and cancelled independently of the membership, so its cancel CTA
  // must render in EVERY branch below. An expired or cancelled membership does not cancel the box:
  // hiding the CTA there left members billed for a box they had no way to stop from this page.
  const boxCancelLinks = visibleBoxSubscriptions?.map((boxSub) => (
    <ManageMembershipLink
      key={boxSub.id}
      onClickCTA={() => handleClickCancelBoxSubscriptionCTA(boxSub)}
      title={getCancelBoxSubscriptionLabel(boxSub)}
      subtitle={CANCEL_SUBSCRIPTION_AND_BOX_COPY}
      isButton
    />
  ));

  const getLinks = () => {
    if (isClubMember) {
      if (isCanceled || isExpired) {
        return (
          <>
            <ManageMembershipLink
              onClickCTA={() => setIsReactivateModalOpen(true)}
              title="Reactivate Membership"
              subtitle="Reactivate today and gain instant access to all of the FabFitFun perks."
              isButton
            />
            {boxCancelLinks}
          </>
        );
      }
      return (
        <>
          {hasVisibleBoxSubscriptions ? (
            boxCancelLinks
          ) : (
            <ManageMembershipLink
              onClickCTA={handleClickCancelClubMembershipCTA}
              title={CANCEL_CLUB_MEMBERSHIP.label}
              subtitle="By canceling your membership you will lose access to your FabFitFun benefits."
              isButton
            />
          )}
        </>
      );
    }

    if (isExpired) {
      return (
        <>
          <ManageMembershipLink
            path={`/reactivate-my-membership${REFERRER_QUERY_STRING}`}
            onClickCTA={trackClickReactivateMembership}
            title={REACTIVATE_MEMBERSHIP_LABEL}
            subtitle="Reactivate to a seasonal or annual membership today and gain
          instant access to all of the FabFitFun perks."
          />
          {boxCancelLinks}
        </>
      );
    }

    return (
      <>
        {isPriceLockEligible && (
          <ManageMembershipLink
            path={LOCK_IN_RATE_PATH}
            title="Lock in Your Rate"
            subtitle="Limited time offer. Lock in your current
              price before the membership price increases."
          />
        )}
        {!isCanceled ? (
          <ManageMembershipLink
            path={`/cancel-subscription${REFERRER_QUERY_STRING}`}
            title={cancelSubscriptionCopy}
            subtitle={cancelSubscriptionSubcopy}
          />
        ) : (
          <ManageMembershipLink
            path={`/reactivate-today${REFERRER_QUERY_STRING}`}
            title={REACTIVATE_MEMBERSHIP_LABEL}
            subtitle="Reactivate to a seasonal or annual membership today and gain
                instant access to all of the FabFitFun perks."
          />
        )}
        {boxCancelLinks}
      </>
    );
  };

  const onSubscriptionChangeDone = () => {
    refetchClubMemberSubscription();
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const onClubMembershipChangeDone = () => {
    trackClickCancelSubscription(
      CANCEL_FLOW_EVENT_NAME.completed,
      CANCEL_CLUB_MEMBERSHIP.label,
      CANCEL_CLUB_MEMBERSHIP.type,
    );
    onSubscriptionChangeDone();
  };

  // Takes the subscription for the same reason the started handler does: the completed event must
  // report the box the member actually cancelled. The hardcoded `CANCEL_BOX_SUBSCRIPTION.label`
  // this replaced reported every completion as a Beauty Edit Box, mislabelling PupBox cancels.
  const onBoxSubscriptionChangeDone = (boxSub: CancellableBoxSubscription) => {
    setCanceledBoxIds((prev) => (prev.includes(boxSub.id) ? prev : [...prev, boxSub.id]));
    trackClickCancelSubscription(
      CANCEL_FLOW_EVENT_NAME.completed,
      getCancelBoxSubscriptionLabel(boxSub),
      CANCEL_BOX_SUBSCRIPTION.type,
    );
    onSubscriptionChangeDone();
  };

  return (
    <div className={`${manageMembershipStyles.container}`}>
      {getLinks()}
      {clubMemberSubscriptionId && (
        <ClubMembershipModal
          isOpen={isCancelMembershipModalOpen}
          subscriptionId={clubMemberSubscriptionId}
          onClose={() => setIsCancelMembershipModalOpen(false)}
          variant="cancel"
          onDone={onClubMembershipChangeDone}
        />
      )}
      {visibleBoxSubscriptions &&
        visibleBoxSubscriptions.map((boxSub) => (
          <ClubMembershipModal
            key={boxSub.id}
            isOpen={cancelBoxModalStates[boxSub.id] || false}
            subscriptionId={boxSub.id}
            onClose={() => setCancelBoxModalStates((prev) => ({ ...prev, [boxSub.id]: false }))}
            variant="cancel"
            onDone={() => onBoxSubscriptionChangeDone(boxSub)}
            isBoxSubscription
            boxSubscriptionName={boxSub.name}
          />
        ))}
      {clubMemberSubscriptionId && (
        <ClubMembershipModal
          isOpen={isReactivateModalOpen}
          subscriptionId={clubMemberSubscriptionId}
          onClose={() => setIsReactivateModalOpen(false)}
          variant="reactivate"
          onDone={onSubscriptionChangeDone}
        />
      )}
    </div>
  );
};

export default ManageMembership;
